/**
 * Outil « Balance des équations » (Boîte à outils, 5e → 2nde).
 *
 * L'équation ax + b = cx + d posée sur une balance : boîtes x et poids
 * (blocs de 10, carrés de 1) sur les plateaux ; un terme négatif est un
 * BALLON qui tire son plateau vers le haut. Chaque geste s'applique aux deux
 * membres : ± k, ± kx, ÷ k ou ÷ (−k) si chaque membre se partage exactement,
 * × (−1), échanger. Historique, annuler, « Un conseil » jouable d'un geste ;
 * résolue, la solution est vérifiée dans l'équation de départ (et, pour une
 * inéquation, une valeur dans S et une hors de S). « Essayer une valeur » :
 * la balance penche du côté le plus lourd, à l'équilibre pour la solution.
 * Niveaux : 5e opérations inverses (pas de × (−1)), 4e ax + b = c relatifs,
 * 3e inéquations ax + b ≥ c, 2nde notation S = ]−∞ ; −4]. Calculs : `balance.ts`.
 */

import { useState, type ReactNode } from 'react';
import { Lightbulb, Shuffle, Undo2 } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  IconButton,
  Note,
  NumberField,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import { fraction } from '../random-launcher/launcher';
import {
  apply,
  boundary,
  degenerate,
  eqPlain,
  eqTex,
  evalSide,
  fractionalSolution,
  generate,
  hint,
  holds,
  moveLabel,
  refusal,
  sideTex,
  solved,
  startFor,
  type Eq,
  type Move,
  type Rel,
  type Side,
} from './balance';

const X_FILL = 'var(--color-primary-500)';
/** poids : gris fixe, liseré clair pour le thème sombre (lettres blanches) */
const UNIT_FILL = '#57534e';
const UNIT_EDGE = 'var(--color-ink-500)';
const NEG = 'var(--color-error-600)';
const NNBSP = String.fromCharCode(0x202f);

/** une solution : entier (den = 1) ou fraction réduite, signe au numérateur */
interface Value {
  num: number;
  den: number;
}

const fmtSigned = (v: number) => (v < 0 ? `−${-v}` : String(v));
const valText = (v: Value) => {
  if (v.den === 1) return fmtSigned(v.num);
  const f = fraction(Math.abs(v.num), v.den);
  return `${v.num < 0 ? '−' : ''}${f.plain}`;
};
/** un membre évalué en x = p/q, en fraction exacte */
const evalAt = (s: Side, v: Value): Value => {
  const num = s.x * v.num + s.c * v.den;
  const g = gcd(Math.abs(num), v.den);
  return { num: num / g + 0, den: v.den / g };
};
function gcd(a: number, b: number): number {
  while (b) [a, b] = [b, a % b];
  return a || 1;
}
const cmp = (a: Value, b: Value) => a.num * b.den - b.num * a.den;
const relHolds = (rel: Rel, d: number) =>
  rel === '=' ? d === 0 : rel === '<' ? d < 0 : rel === '>' ? d > 0 : rel === '≤' ? d <= 0 : d >= 0;

/** « −5 » pour la feuille : ['−', '5'] ; « 3/2 » : ['3', '÷', '2'] */
function valueAtoms(v: Value): string[] {
  const n = Math.abs(v.num);
  const body = v.den === 1 ? [String(n)] : [String(n), '÷', String(v.den)];
  return v.num < 0 ? ['−', ...body] : body;
}

export function BalanceTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const rank = Math.min(5, schoolLevelRank(level) ?? 5);
  const [kind, setKind] = useState<'eq' | 'ineq'>('eq');
  const [history, setHistory] = useState<Array<{ eq: Eq; move?: Move }>>(() => [
    { eq: startFor(rank) },
  ]);
  const [k, setK] = useState(1);
  const [msg, setMsg] = useState<string | null>(null);
  const [tip, setTip] = useState<{ text: string; move?: Move } | null>(null);
  const [edit, setEdit] = useState(false);
  const [trying, setTrying] = useState(false);
  const [tryX, setTryX] = useState(0);
  const [fracDone, setFracDone] = useState<{ rel: Rel; value: Value } | null>(null);

  const start = history[0]!.eq;
  const e = history[history.length - 1]!.eq;
  const whole = solved(e);
  const done = whole ? { rel: whole.rel, value: { num: whole.value, den: 1 } } : fracDone;
  const dead = degenerate(e);
  const frac = fractionalSolution(e);

  const clear = () => {
    setMsg(null);
    setTip(null);
    setFracDone(null);
  };
  const reset = (next: Eq) => {
    setHistory([{ eq: next }]);
    clear();
    // le curseur d'essai part de la frontière (arrondie) : la solution est à portée
    const b = boundary(next);
    setTryX(b === null ? 0 : Math.max(-30, Math.min(30, Math.round(b) - 2)));
  };
  const play = (m: Move) => {
    const next = apply(e, m);
    if (!next) {
      setMsg(refusal(e, m));
      return;
    }
    setHistory((h) => [...h, { eq: next, move: m }]);
    clear();
  };
  const askHint = () => {
    const h = hint(e, rank);
    if (!h) return;
    if (!apply(e, h.move) && frac) {
      setTip({
        text: `La division ne tombe pas juste : la solution n’est pas un nombre entier, c’est la fraction x ${frac.rel} ${valText({ num: frac.num, den: frac.den })}.`,
      });
      return;
    }
    setTip({ text: h.why, move: h.move });
  };
  const fresh = (ineq = kind === 'ineq') => {
    reset(generate(rank, rng, ineq));
    setTrying(false);
  };

  // la balance penche du côté le plus lourd : essai d'une valeur, membre sans x, ou inégalité
  const diff = trying ? evalSide(e.l, tryX) - evalSide(e.r, tryX) : 0;
  const tilt = trying
    ? diff === 0
      ? 0
      : Math.sign(diff) * Math.max(4, Math.min(12, Math.abs(diff) * 1.5))
    : dead
      ? Math.sign(e.l.c - e.r.c) * 8
      : e.rel === '>' || e.rel === '≥'
        ? 6
        : e.rel === '<' || e.rel === '≤'
          ? -6
          : 0;
  const tryMin = Math.round(boundary(start) ?? 0) - 10;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {rank >= 4 && (
          <Segmented
            ariaLabel="Type"
            value={kind}
            options={[
              { value: 'eq', label: 'Équation' },
              { value: 'ineq', label: 'Inéquation' },
            ]}
            onChange={(v) => {
              setKind(v);
              fresh(v === 'ineq');
            }}
          />
        )}
        <ToggleChip on={edit} onClick={() => setEdit((v) => !v)}>
          Écrire la mienne
        </ToggleChip>
        <IconButton
          label={kind === 'ineq' ? 'Nouvelle inéquation' : 'Nouvelle équation'}
          onClick={() => fresh()}
        >
          <Shuffle className="h-5 w-5" aria-hidden />
        </IconButton>
      </div>

      {edit && (
        <EquationEditor
          eq={start}
          inequality={kind === 'ineq'}
          onChange={(next) => {
            reset(next);
            setTrying(false);
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <MathText className="text-ink-900 min-w-0 flex-1 overflow-x-auto text-2xl">{`$${eqTex(e)}$`}</MathText>
        <IconButton
          label="Annuler le dernier geste"
          disabled={history.length < 2}
          onClick={() => {
            setHistory((h) => (h.length > 1 ? h.slice(0, -1) : h));
            clear();
          }}
        >
          <Undo2 className="h-5 w-5" aria-hidden />
        </IconButton>
      </div>
      <p className="sr-only" aria-live="polite">
        {eqPlain(e)}
      </p>

      <BalanceFigure eq={e} tilt={tilt} tryX={trying ? tryX : null} inequality={e.rel !== '='} />

      {!done && !dead && (
        <section className="space-y-2" aria-label="Gestes sur les deux plateaux">
          <p className="text-ink-600 text-sm">Le même geste sur les deux plateaux :</p>
          <Stepper label="Nombre k" value={k} min={1} max={9} onChange={setK} />
          <div className="grid grid-cols-3 gap-2">
            <MoveButton
              label={`Enlever ${k} aux deux membres`}
              onClick={() => play({ kind: 'add', k: -k })}
            >
              − {k}
            </MoveButton>
            <MoveButton
              label={`Ajouter ${k} aux deux membres`}
              onClick={() => play({ kind: 'add', k })}
            >
              + {k}
            </MoveButton>
            <MoveButton
              label={`Diviser les deux membres par ${k}`}
              onClick={() => play({ kind: 'div', k })}
              disabled={k === 1}
            >
              ÷ {k}
            </MoveButton>
            <MoveButton
              label={`Enlever ${k === 1 ? 'x' : `${k}x`} aux deux membres`}
              onClick={() => play({ kind: 'addX', k: -k })}
            >
              − {k === 1 ? '' : k}x
            </MoveButton>
            <MoveButton
              label={`Ajouter ${k === 1 ? 'x' : `${k}x`} aux deux membres`}
              onClick={() => play({ kind: 'addX', k })}
            >
              + {k === 1 ? '' : k}x
            </MoveButton>
            {rank >= 3 ? (
              <MoveButton
                label={`Diviser les deux membres par moins ${k}`}
                onClick={() => play({ kind: 'div', k: -k })}
              >
                ÷ (−{k})
              </MoveButton>
            ) : (
              <span />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {rank >= 3 && (
              <MoveButton
                label="Multiplier les deux membres par moins 1"
                onClick={() => play({ kind: 'neg' })}
                wide
              >
                × (−1)
              </MoveButton>
            )}
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => play({ kind: 'swap' })}
            >
              ⇄ Échanger les plateaux
            </button>
            <button
              type="button"
              className={cn(SECONDARY_BUTTON, 'inline-flex items-center gap-1.5')}
              onClick={askHint}
            >
              <Lightbulb className="h-4 w-4" aria-hidden />
              Un conseil
            </button>
          </div>
        </section>
      )}

      <div aria-live="polite" className="space-y-2">
        {msg && (
          <Note tone="error" live={false}>
            {msg}
          </Note>
        )}
        {tip && (
          <div className="bg-ink-50 space-y-2 rounded-2xl px-3 py-2" data-testid="balance-tip">
            <p className="text-ink-800 text-sm">{tip.text}</p>
            {tip.move && (
              <button type="button" className={PRIMARY_BUTTON} onClick={() => play(tip.move!)}>
                Faire ce geste ({moveLabel(tip.move)})
              </button>
            )}
            {!tip.move && frac && (
              <button
                type="button"
                className={PRIMARY_BUTTON}
                onClick={() => {
                  setFracDone({ rel: frac.rel, value: { num: frac.num, den: frac.den } });
                  setTip(null);
                }}
              >
                Conclure avec la fraction
              </button>
            )}
          </div>
        )}
        {done && (
          <SolvedPanel
            start={start}
            rel={done.rel}
            value={done.value}
            rank={rank}
            onSend={onSend}
            sendCost={sendCost}
          />
        )}
        {dead && (
          <Note tone={dead === 'always' ? 'success' : 'error'} live={false}>
            {dead === 'always'
              ? `Il n’y a plus de x et ${eqPlain(e)} est toujours vrai : tous les nombres sont solutions.`
              : `Il n’y a plus de x et ${eqPlain(e)} est faux : il n’y a aucune solution.`}
          </Note>
        )}
      </div>

      {history.length > 1 && (
        <section className="border-ink-100 rounded-2xl border px-3 py-2" aria-label="Étapes">
          <ol className="space-y-1" data-testid="balance-history">
            {history.map((h, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <MathText className="text-ink-900 min-w-0 flex-1 overflow-x-auto">{`$${eqTex(h.eq)}$`}</MathText>
                {history[i + 1]?.move && (
                  <span className="text-primary-700 shrink-0 text-sm font-semibold">
                    {moveLabel(history[i + 1]!.move!)}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="space-y-2" aria-label="Essayer une valeur">
        <ToggleChip on={trying} onClick={() => setTrying((v) => !v)}>
          Essayer une valeur
        </ToggleChip>
        {trying && (
          <>
            <ParamSlider
              name="x vaut"
              ariaLabel="Valeur essayée pour x"
              value={tryX}
              min={tryMin}
              max={tryMin + 20}
              step={1}
              format={fmtSigned}
              onChange={setTryX}
            />
            <p className="text-ink-700 text-sm" data-testid="balance-try" aria-live="polite">
              Pour x = {fmtSigned(tryX)} : à gauche {fmtSigned(evalSide(e.l, tryX))}, à droite{' '}
              {fmtSigned(evalSide(e.r, tryX))}.{' '}
              {diff === 0
                ? 'Équilibre !'
                : `La balance penche ${diff > 0 ? 'à gauche' : 'à droite'}.`}{' '}
              {holds(e, tryX)
                ? `${e.rel === '=' ? 'C’est la solution.' : 'Cette valeur est une solution.'}`
                : `${e.rel === '=' ? 'Ce n’est pas la solution.' : 'Cette valeur n’est pas une solution.'}`}
            </p>
          </>
        )}
      </section>
    </div>
  );
}

function MoveButton({
  label,
  onClick,
  disabled = false,
  wide = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'border-ink-200 text-ink-900 hover:bg-ink-50 font-display min-h-11 rounded-xl border text-lg font-bold tabular-nums active:scale-[0.98] disabled:opacity-40',
        wide && 'px-4',
      )}
    >
      {children}
    </button>
  );
}

/** « 2 × 5 + 3 » : un membre avec x remplacé (espaces insécables) */
function substituted(s: Side, v: Value): string {
  const t = valText(v);
  const vv = v.num < 0 || v.den > 1 ? `(${t})` : t;
  const xs =
    s.x === 0
      ? ''
      : s.x === 1
        ? vv
        : s.x === -1
          ? `−${vv}`
          : `${fmtSigned(s.x)}${NNBSP}×${NNBSP}${vv}`;
  if (!xs) return fmtSigned(s.c);
  if (s.c === 0) return xs;
  return `${xs}${NNBSP}${s.c < 0 ? '−' : '+'}${NNBSP}${Math.abs(s.c)}`;
}

/** S = ]−∞ ; −4] (2nde) */
function interval(rel: Rel, v: Value): string {
  const t = valText(v);
  if (rel === '=') return `S = {${t}}`;
  const inf = '∞';
  return rel === '<'
    ? `S = ]−${inf}${NNBSP};${NNBSP}${t}[`
    : rel === '≤'
      ? `S = ]−${inf}${NNBSP};${NNBSP}${t}]`
      : rel === '>'
        ? `S = ]${t}${NNBSP};${NNBSP}+${inf}[`
        : `S = [${t}${NNBSP};${NNBSP}+${inf}[`;
}

function SolvedPanel({
  start,
  rel,
  value,
  rank,
  onSend,
  sendCost,
}: {
  start: Eq;
  rel: Rel;
  value: Value;
  rank: number;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const relWord: Record<Rel, string> = {
    '=': 'égal à',
    '<': 'inférieurs à',
    '>': 'supérieurs à',
    '≤': 'inférieurs ou égaux à',
    '≥': 'supérieurs ou égaux à',
  };
  // pour une inéquation : la frontière, une valeur dans S, une valeur hors de S
  const below: Value = { num: value.num - value.den, den: value.den };
  const above: Value = { num: value.num + value.den, den: value.den };
  const inS = rel === '<' || rel === '≤' ? below : above;
  const outS = rel === '<' || rel === '≤' ? above : below;
  const checks: Array<{ v: Value; tag: string }> =
    rel === '='
      ? [{ v: value, tag: '' }]
      : [
          { v: value, tag: 'frontière' },
          { v: inS, tag: 'dans S' },
          { v: outS, tag: 'hors de S' },
        ];
  return (
    <div className="bg-success-50 space-y-2 rounded-2xl px-3 py-3" data-testid="balance-solved">
      <p className="text-success-600 text-lg font-bold">
        x {rel} {valText(value)}
        {rank >= 5 && (
          <span className="text-ink-800 ml-3 text-base font-semibold">{interval(rel, value)}</span>
        )}
      </p>
      {rel !== '=' && (
        <p className="text-ink-800 text-sm">
          Les solutions sont tous les nombres {relWord[rel]} {valText(value)}.
        </p>
      )}
      <p className="text-ink-700 text-sm">
        Vérification dans {rel === '=' ? 'l’équation' : 'l’inéquation'} de départ :
      </p>
      <ul className="text-ink-800 space-y-0.5 text-sm" data-testid="balance-checks">
        {checks.map(({ v, tag }) => {
          const l = evalAt(start.l, v);
          const r = evalAt(start.r, v);
          const ok = relHolds(start.rel, cmp(l, r));
          return (
            <li key={tag}>
              x = {valText(v)}
              {tag && ` (${tag})`} : {substituted(start.l, v)} = {valText(l)} et{' '}
              {substituted(start.r, v)} = {valText(r)}
              {rel === '='
                ? ''
                : ` : ${valText(l)} ${start.rel} ${valText(r)} est ${ok ? 'vrai' : 'faux'}`}{' '}
              {ok ? '✓' : '✗'}
            </li>
          );
        })}
      </ul>
      {onSend && rel === '=' && (
        <SendToSheetButton
          label={valText(value)}
          cost={sendCost}
          onClick={() => onSend({ atoms: valueAtoms(value) })}
        />
      )}
    </div>
  );
}

/** Écrire son équation : ax + b R cx + d. */
function EquationEditor({
  eq,
  inequality,
  onChange,
}: {
  eq: Eq;
  inequality: boolean;
  onChange: (e: Eq) => void;
}) {
  const set = (patch: Partial<{ a: number; b: number; c: number; d: number; rel: Rel }>) => {
    const v = { a: eq.l.x, b: eq.l.c, c: eq.r.x, d: eq.r.c, rel: eq.rel, ...patch };
    onChange({ l: { x: v.a, c: v.b }, r: { x: v.c, c: v.d }, rel: inequality ? v.rel : '=' });
  };
  return (
    <section className="border-ink-100 space-y-2 rounded-2xl border p-2" aria-label="Mon équation">
      <MathText className="text-ink-600 block text-sm">{`$ax + b \\;${inequality ? '\\lessgtr' : '='}\\; cx + d$`}</MathText>
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="a" value={eq.l.x} min={-9} max={9} onChange={(a) => set({ a })} />
        <NumberField label="b" value={eq.l.c} min={-40} max={40} onChange={(b) => set({ b })} />
        <NumberField label="c" value={eq.r.x} min={-9} max={9} onChange={(c) => set({ c })} />
        <NumberField label="d" value={eq.r.c} min={-40} max={40} onChange={(d) => set({ d })} />
      </div>
      {inequality && (
        <Segmented
          ariaLabel="Relation"
          value={eq.rel === '=' ? '<' : eq.rel}
          options={(['<', '≤', '>', '≥'] as const).map((r) => ({ value: r, label: r }))}
          onChange={(rel) => set({ rel })}
        />
      )}
    </section>
  );
}

const W = 320;
const PX = 160;
const PY = 74;
const HALF = 98;
const PLATE_W = 112;

interface Item {
  w: number;
  h: number;
  kind: 'x' | 'ten' | 'one' | 'big';
  label: string;
}

/** les objets POSITIFS d'un plateau : poids (10 et 1) en bas, boîtes x au-dessus */
function itemsOf(s: Side): Item[] {
  const c = Math.max(0, s.c);
  const weights: Item[] =
    c > 49
      ? [{ w: 40, h: 22, kind: 'big', label: String(c) }]
      : [
          ...Array.from({ length: Math.floor(c / 10) }, () => ({
            w: 22,
            h: 22,
            kind: 'ten' as const,
            label: '10',
          })),
          ...Array.from({ length: c % 10 }, () => ({
            w: 10,
            h: 10,
            kind: 'one' as const,
            label: '',
          })),
        ];
  const x = Math.max(0, s.x);
  // au-delà de 6, une seule boîte « 9x » : la pile reste sous le fléau
  const xs: Item[] =
    x > 6
      ? [{ w: 40, h: 22, kind: 'x', label: `${x}x` }]
      : Array.from({ length: x }, () => ({ w: 20, h: 20, kind: 'x' as const, label: 'x' }));
  return [...weights, ...xs];
}

/** les ballons : un par terme négatif (« −3x », « −15 ») */
function balloonsOf(s: Side): string[] {
  return [...(s.x < 0 ? [s.x === -1 ? '−x' : `−${-s.x}x`] : []), ...(s.c < 0 ? [`−${-s.c}`] : [])];
}

/** rangées de gauche à droite, de bas en haut ; un nouveau type commence une rangée */
function layout(items: Item[], width: number) {
  const out: Array<Item & { dx: number; dy: number }> = [];
  let x = 0;
  let y = 0;
  let rowH = 0;
  let prev: Item['kind'] | null = null;
  for (const it of items) {
    const newKind = prev !== null && (prev === 'x') !== (it.kind === 'x');
    if (x > 0 && (x + it.w > width || newKind)) {
      x = 0;
      y += rowH + 3;
      rowH = 0;
    }
    out.push({ ...it, dx: x, dy: y });
    x += it.w + 3;
    rowH = Math.max(rowH, it.h);
    prev = it.kind;
  }
  const rows = new Map<number, Array<Item & { dx: number; dy: number }>>();
  for (const o of out) rows.set(o.dy, [...(rows.get(o.dy) ?? []), o]);
  for (const row of rows.values()) {
    const last = row[row.length - 1]!;
    const shift = (width - (last.dx + last.w)) / 2;
    for (const o of row) o.dx += shift;
  }
  const height = out.reduce((m, o) => Math.max(m, o.dy + o.h), 0);
  return { items: out, height };
}

function BalanceFigure({
  eq,
  tilt,
  tryX,
  inequality,
}: {
  eq: Eq;
  tilt: number;
  tryX: number | null;
  inequality: boolean;
}) {
  const t = (tilt * Math.PI) / 180;
  const stacks = [eq.l, eq.r].map((s) => layout(itemsOf(s), PLATE_W - 12));
  // les plateaux pendent assez bas pour que la plus haute pile reste sous le fléau
  const drop = Math.max(70, Math.max(...stacks.map((s) => s.height)) + 30);
  const H = PY + 22 + drop + 34;
  const ends = [
    { x: PX - HALF * Math.cos(t), y: PY + HALF * Math.sin(t), side: eq.l },
    { x: PX + HALF * Math.cos(t), y: PY - HALF * Math.sin(t), side: eq.r },
  ];
  const tryNeg = tryX !== null && tryX < 0;
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Balance : à gauche ${sideTex(eq.l).replace(/-/g, 'moins ')}, à droite ${sideTex(eq.r).replace(/-/g, 'moins ')}${tilt === 0 ? ', à l’équilibre' : tilt > 0 ? ', elle penche à gauche' : ', elle penche à droite'}.`}
        data-testid="balance-figure"
      >
        <path
          d={`M ${PX} ${PY} L ${PX - 26} ${H - 12} L ${PX + 26} ${H - 12} Z`}
          fill="var(--color-ink-200)"
          stroke="var(--color-ink-400)"
          strokeWidth={1.5}
        />
        <line
          x1={PX - 60}
          x2={PX + 60}
          y1={H - 11}
          y2={H - 11}
          stroke="var(--color-ink-400)"
          strokeWidth={3}
          strokeLinecap="round"
        />
        {ends.map((end, i) => {
          const py = end.y + drop;
          const { items } = stacks[i]!;
          const value = tryX === null ? null : evalSide(end.side, tryX);
          const balloons = balloonsOf(end.side);
          return (
            <g key={i}>
              {/* ballons : ils tirent le plateau vers le haut */}
              {balloons.map((label, j) => {
                const bx = end.x + (balloons.length === 2 ? (j === 0 ? -26 : 26) : 0);
                const by = end.y - 40;
                const rx = 8 + label.length * 4;
                return (
                  <g key={label}>
                    <line
                      x1={bx}
                      y1={by + 13}
                      x2={end.x + (balloons.length === 2 && j === 1 ? 1 : -1) * (PLATE_W / 2 - 6)}
                      y2={py - 1}
                      stroke={NEG}
                      strokeWidth={1}
                      strokeDasharray="2 2"
                    />
                    <ellipse
                      cx={bx}
                      cy={by}
                      rx={rx}
                      ry={13}
                      fill="var(--fig-bg, #fff)"
                      stroke={NEG}
                      strokeWidth={2}
                    />
                    <path
                      d={`M ${bx - 3} ${by + 15} L ${bx + 3} ${by + 15} L ${bx} ${by + 12} Z`}
                      fill={NEG}
                    />
                    <text
                      x={bx}
                      y={by + 4.5}
                      textAnchor="middle"
                      fontSize={12}
                      fontWeight={700}
                      fill={NEG}
                    >
                      {label}
                    </text>
                  </g>
                );
              })}
              <line
                x1={end.x}
                y1={end.y}
                x2={end.x - PLATE_W / 2}
                y2={py}
                stroke="var(--color-ink-400)"
                strokeWidth={1.25}
              />
              <line
                x1={end.x}
                y1={end.y}
                x2={end.x + PLATE_W / 2}
                y2={py}
                stroke="var(--color-ink-400)"
                strokeWidth={1.25}
              />
              <path
                d={`M ${end.x - PLATE_W / 2 - 4} ${py} Q ${end.x} ${py + 12} ${end.x + PLATE_W / 2 + 4} ${py}`}
                fill="var(--color-ink-200)"
                stroke="var(--color-ink-500)"
                strokeWidth={2}
              />
              {items.map((it, j) => {
                const x = end.x - (PLATE_W - 12) / 2 + it.dx;
                const y = py - 2 - it.dy - it.h;
                const isX = it.kind === 'x';
                // une boîte x essayée avec une valeur négative n'est plus un poids : pointillés
                const ghost = isX && tryNeg;
                const fill = isX ? X_FILL : UNIT_FILL;
                return (
                  <g key={j}>
                    <rect
                      x={x}
                      y={y}
                      width={it.w}
                      height={it.h}
                      rx={it.kind === 'one' ? 2 : 4}
                      fill={ghost ? 'var(--fig-bg, #fff)' : fill}
                      stroke={ghost ? NEG : isX ? fill : UNIT_EDGE}
                      strokeWidth={ghost ? 1.5 : 1}
                      strokeDasharray={ghost ? '3 2' : undefined}
                    />
                    {it.label && (
                      <text
                        x={x + it.w / 2}
                        y={y + it.h / 2 + 4.5}
                        textAnchor="middle"
                        fontSize={12}
                        fontWeight={700}
                        fill={ghost ? NEG : '#ffffff'}
                      >
                        {isX && tryX !== null && it.label === 'x' ? fmtSigned(tryX) : it.label}
                      </text>
                    )}
                  </g>
                );
              })}
              {value !== null && (
                <text
                  x={end.x}
                  y={py + 28}
                  textAnchor="middle"
                  fontSize={13}
                  fontWeight={700}
                  fill="var(--color-ink-900)"
                >
                  = {fmtSigned(value)}
                </text>
              )}
            </g>
          );
        })}
        {/* le fléau par-dessus les fils des ballons */}
        <line
          x1={ends[0]!.x}
          y1={ends[0]!.y}
          x2={ends[1]!.x}
          y2={ends[1]!.y}
          stroke="var(--color-ink-700)"
          strokeWidth={5}
          strokeLinecap="round"
        />
        <circle cx={PX} cy={PY} r={6} fill="var(--color-ink-700)" />
      </svg>
      <p className="text-ink-500 mt-1 text-center text-xs">
        Boîte bleue : x ; carré : 1 ; bloc : 10 ; ballon rouge : un terme négatif, il tire le
        plateau vers le haut.
        {inequality &&
          ' Pour une inéquation, la balance penche comme l’inégalité : c’est vrai pour les valeurs solutions.'}
      </p>
    </div>
  );
}
